import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  Availability, GatewayConfig, GatewayConfigList, GatewayConfigRequest, PayPage, PayResult, PaymentAttempt, PaymentLink,
  PaymentLinkPage, PaymentLinkStatus, AttemptStatus, PaymentProvider, Payout, PayoutCandidates, PayoutRequest,
  SuggestedAccountsResult,
} from '../interfaces/sales/paymentLink.interface';

export const PaymentLinkService = {
  list: async (institutionId: string, status?: PaymentLinkStatus, page = 0, size = 20): Promise<PaymentLinkPage> =>
    (await axios.get(ENDPOINTS.PAYMENT_LINK.LIST(institutionId), { params: { ...(status ? { status } : {}), page, size } })).data,
  availability: async (institutionId: string): Promise<Availability> =>
    (await axios.get(ENDPOINTS.PAYMENT_LINK.AVAILABILITY(institutionId))).data,
  attempts: async (institutionId: string, status?: AttemptStatus): Promise<PaymentAttempt[]> =>
    (await axios.get(ENDPOINTS.PAYMENT_LINK.ATTEMPTS(institutionId), { params: status ? { status } : {} })).data,
  forInvoice: async (invoiceId: string): Promise<PaymentLink[]> =>
    (await axios.get(ENDPOINTS.PAYMENT_LINK.BY_INVOICE(invoiceId))).data,
  /** The returned `url` is the raw link and is shown once: it is never stored. */
  create: async (salesInvoiceId: string, expiryDays?: number): Promise<PaymentLink> =>
    (await axios.post(ENDPOINTS.PAYMENT_LINK.BASE, { salesInvoiceId, expiryDays })).data,
  createAndSend: async (salesInvoiceId: string, expiryDays?: number): Promise<PaymentLink> =>
    (await axios.post(ENDPOINTS.PAYMENT_LINK.SEND, { salesInvoiceId, expiryDays })).data,
  cancel: async (id: string, reason?: string): Promise<PaymentLink> =>
    (await axios.post(ENDPOINTS.PAYMENT_LINK.CANCEL(id), { reason })).data,
  reverify: async (attemptId: string): Promise<{ outcome: string }> =>
    (await axios.post(ENDPOINTS.PAYMENT_LINK.REVERIFY(attemptId))).data,
  /** Terminal "Mark resolved": the manual receipt that records the money, or a note (refunded / rejected). */
  resolve: async (attemptId: string, body: { receiptId?: string; note?: string }): Promise<void> => {
    await axios.post(ENDPOINTS.PAYMENT_LINK.RESOLVE(attemptId), body);
  },
};

export const GatewayConfigService = {
  list: async (institutionId: string): Promise<GatewayConfigList> =>
    (await axios.get(ENDPOINTS.PAYMENT_GATEWAY_CONFIG.LIST(institutionId))).data,
  save: async (dto: GatewayConfigRequest): Promise<GatewayConfig> =>
    (await axios.put(ENDPOINTS.PAYMENT_GATEWAY_CONFIG.BASE, dto)).data,
  suggestedAccounts: async (institutionId: string): Promise<SuggestedAccountsResult> =>
    (await axios.post(ENDPOINTS.PAYMENT_GATEWAY_CONFIG.SUGGESTED_ACCOUNTS(institutionId))).data,
};

export const GatewayPayoutService = {
  list: async (institutionId: string): Promise<Payout[]> =>
    (await axios.get(ENDPOINTS.GATEWAY_PAYOUT.LIST(institutionId))).data,
  get: async (id: string): Promise<Payout> => (await axios.get(ENDPOINTS.GATEWAY_PAYOUT.ONE(id))).data,
  candidates: async (institutionId: string, provider: PaymentProvider): Promise<PayoutCandidates> =>
    (await axios.get(ENDPOINTS.GATEWAY_PAYOUT.CANDIDATES(institutionId), { params: { provider } })).data,
  create: async (dto: PayoutRequest): Promise<Payout> => (await axios.post(ENDPOINTS.GATEWAY_PAYOUT.BASE, dto)).data,
  reverse: async (id: string): Promise<Payout> => (await axios.post(ENDPOINTS.GATEWAY_PAYOUT.REVERSE(id))).data,
};

/** Unauthenticated: the token in the URL is the capability. */
export const PublicPayService = {
  details: async (token: string): Promise<PayPage> => (await axios.get(ENDPOINTS.PUBLIC_PAY.DETAILS(token))).data,
  start: async (token: string, provider: PaymentProvider): Promise<{ redirectUrl: string }> =>
    (await axios.post(ENDPOINTS.PUBLIC_PAY.START(token, provider.toLowerCase()))).data,
  result: async (ref: string): Promise<PayResult> => (await axios.get(ENDPOINTS.PUBLIC_PAY.RESULT(ref))).data,
};
